// The content owner's segment picker (E32-S9-T3; ADR-032), ported from mockups/production-picker.html
// (T0 approved it in PR #495, with the change that the page says "organisation", never "tenant").
//
// The page reads GET /v1/production-plan (the saved plan, its summary and the catalogue), lets the
// owner build a draft selection against the 50:00 budget, and saves it with PUT carrying the version
// it read. The budget arithmetic is plan-model.ts; the server decides again on save. Four answers to a
// save are handled: 200 (saved, shown with who and when), 409 (someone else saved first: reload),
// 422 (refused, with the figures) and 403 (read-only).
import type { Principal } from "@waypoint/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { isApiError, ValidationError } from "../../api/errors.js";
import { useApi } from "../../api/provider.js";
import { t } from "../../i18n/t.js";
import { EmptyState, Page } from "../page.js";
import {
  entriesOf,
  keysOf,
  mmss,
  PLAN_PATH,
  PlanRead,
  PlanWritten,
  segmentOf,
  usedOf,
  type Ledger,
} from "./plan-model.js";
import { PickerView } from "./picker-view.js";

const PLAN_KEY = ["production-plan"] as const;

/** Who may change the plan; the API decides, and a 403 on save turns the screen read-only anyway. */
export function canChangePlan(principal: Principal): boolean {
  return principal.roles.includes("content-owner") || principal.roles.includes("tenant-admin");
}

/** The words for a 422: the figures it names, else the general line. */
export function refusedWords(err: ValidationError): string {
  const over = err.refusals.find((r) => r.code === "plan.over-budget");
  if (over?.usedSeconds === undefined || over.budgetSeconds === undefined)
    return t("plan.refused.other");
  return over.previousUsedSeconds === undefined
    ? t("plan.refused.over", { used: mmss(over.usedSeconds), budget: mmss(over.budgetSeconds) })
    : t("plan.refused.overStored", {
        used: mmss(over.usedSeconds),
        previous: mmss(over.previousUsedSeconds),
        budget: mmss(over.budgetSeconds),
      });
}

export function PickerPage({ principal }: { principal: Principal }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: PLAN_KEY, queryFn: () => api.get(PLAN_PATH, PlanRead) });
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [readOnly, setReadOnly] = useState(false);

  const save = useMutation({
    mutationFn: (input: { version: number; keys: ReadonlySet<string>; ledger: Ledger }) =>
      api.put(PLAN_PATH, PlanWritten, {
        version: input.version,
        segments: [...input.keys].flatMap((key) => {
          const entry = input.ledger.byKey.get(key);
          return entry === undefined ? [] : [segmentOf(entry)];
        }),
      }),
  });

  if (query.isPending)
    return (
      <Page title={t("nav.plan")}>
        <p role="status" aria-busy className="text-wp-text-3">
          {t("plan.loading")}
        </p>
      </Page>
    );
  if (query.isError) {
    const forbidden = isApiError(query.error) && query.error.kind === "forbidden";
    return (
      <Page title={t(forbidden ? "page.forbidden.title" : "plan.failed.title")}>
        <EmptyState
          icon={forbidden ? "lock" : "alert"}
          title={t(forbidden ? "plan.forbidden.line" : "plan.failed.line")}
        />
      </Page>
    );
  }

  const data = query.data;
  const entries = entriesOf(data.catalogue);
  const ledger: Ledger = {
    byKey: new Map(entries.map((e) => [e.key, e])),
    budget: data.summary.budgetSeconds,
    storedUsed: data.summary.usedSeconds,
  };
  const stored = keysOf(data.plan?.segments ?? []);

  const onSave = async (keys: ReadonlySet<string>): Promise<string | undefined> => {
    setNotice(undefined);
    try {
      const written = await save.mutateAsync({ version: data.plan?.version ?? 0, keys, ledger });
      queryClient.setQueryData(PLAN_KEY, { ...data, ...written });
      setNotice(t("plan.saved"));
      return undefined;
    } catch (err) {
      if (isApiError(err) && err.kind === "conflict") {
        setNotice(t("plan.conflict"));
        void query.refetch();
        return t("plan.conflict");
      }
      if (isApiError(err) && err.kind === "forbidden") {
        setReadOnly(true);
        return t("plan.refused.forbidden");
      }
      if (err instanceof ValidationError && err.source === "request") return refusedWords(err);
      return t("plan.refused.failed");
    }
  };

  return (
    <PickerView
      // A fresh draft whenever the saved plan is (re)loaded: after a save and after a conflict.
      key={query.dataUpdatedAt}
      principal={principal}
      data={data}
      entries={entries}
      ledger={ledger}
      stored={stored}
      storedSeconds={usedOf(ledger, stored)}
      readOnly={readOnly || !canChangePlan(principal)}
      saving={save.isPending}
      notice={notice}
      onEdit={() => {
        setNotice(undefined);
      }}
      onSave={onSave}
    />
  );
}
